import {DocumentNode, gql} from '@apollo/client';
import assert from 'assert';
import React from 'react';

import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getPowAgnosticProcedureFromFlowchartVersion} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';

import {ProcedureRunPlanMetadataTabGraphQL as ProcedureRunPlanNode} from './__generated/ProcedureRunPlanMetadataTabGraphQL';
import {MetadataTabLayout} from './MetadataTabLayout';

interface ProcedureRunPlanMetadataTabProps {
  procedureRunPlan: ProcedureRunPlanNode;
}

export const ProcedureRunPlanMetadataTab = ({
  procedureRunPlan,
}: ProcedureRunPlanMetadataTabProps): JSX.Element => {
  const flowchartVersion =
    procedureRunPlan.templateProcedureFlowchartVersion ??
    procedureRunPlan.procedureFlowchartConfigVersionProxy;
  assert(flowchartVersion != null, 'Expected flowchart version to be present.');
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible.');
  assert(!isInaccessible(procedureRunPlan.study), 'Expected study to be accessible.');
  const procedureParent = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion) as {
    __typename: 'ProcedureNode';
    id: string;
    name: string;
  };

  return (
    <MetadataTabLayout
      name={procedureParent.name}
      procedureType={getProcedureTypeForStudyType(procedureRunPlan.study.studySchema.studyType)}
      descriptionEditor={
        <EditableTextField
          isDisabled={true}
          requestChange={() => {}}
          emptyValueAllowed={true}
          value={procedureRunPlan.description}
          type={TextFieldType.TEXTAREA}
        />
      }
      procedureId={procedureParent.id}
      flowchartVersionId={flowchartVersion.id}
      createdAt={procedureRunPlan.createdAt}
    />
  );
};

export const PROCEDURE_RUN_PLAN_METADATA_TAB_FRAGMENT: DocumentNode = gql`
  fragment ProcedureRunPlanMetadataTabGraphQL on ProcedureRunPlanNode {
    id
    description
    createdAt
    study {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
      }
    }
    templateProcedureFlowchartVersion {
      ... on ProcedureFlowchartVersionNode {
        id
        procedureFlowchart {
          id
          parent {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
    procedureFlowchartConfigVersionProxy {
      ... on ProcedureFlowchartConfigVersionProxyNode {
        id
        procedure {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
